import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Package, Calendar, User, Mail, Phone, CreditCard, Tag, CheckCircle2, XCircle, Clock, Search } from 'lucide-react';

interface OrderItem {
  id: string;
  name: string;
  version: string;
  description?: string;
  price: number;
  finalPrice: number;
  discountCode?: string | null;
}

interface Order {
  id: string;
  userId: string;
  email: string;
  fullName: string;
  phone: string;
  amount: number;
  originalAmount: number;
  items: OrderItem[];
  discountCode?: string | null;
  status: string;
  source: string;
  paymentMethod: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export default function CoursedeeOrdersAdmin() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'verified' | 'pending'>('all');

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      
      // Query subscription_payments for coursedee orders
      const ordersRef = collection(db, 'subscription_payments');
      const q = query(
        ordersRef,
        where('source', '==', 'coursedee_cart'),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const ordersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Order[];
      
      setOrders(ordersData);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.phone.includes(searchTerm);
    
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const formatDate = (timestamp: Timestamp) => {
    if (!timestamp) return '-';
    return timestamp.toDate().toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'verified':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
            <CheckCircle2 className="w-4 h-4" />
            ชำระแล้ว
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
            <Clock className="w-4 h-4" />
            รอตรวจสอบ
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
            <XCircle className="w-4 h-4" />
            {status}
          </span>
        );
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'discount_code_100%':
        return 'โค้ดส่วนลด 100%';
      case 'bank_transfer':
        return 'โอนเงิน';
      default:
        return method;
    }
  };

  const totalRevenue = filteredOrders
    .filter(order => order.status === 'verified')
    .reduce((sum, order) => sum + order.amount, 0);

  const totalOrders = filteredOrders.length;
  const verifiedOrders = filteredOrders.filter(order => order.status === 'verified').length;

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">กำลังโหลดข้อมูล...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">คำสั่งซื้อทั้งหมด</p>
              <p className="text-3xl font-bold mt-1">{totalOrders}</p>
            </div>
            <Package className="w-12 h-12 text-purple-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">ชำระแล้ว</p>
              <p className="text-3xl font-bold mt-1">{verifiedOrders}</p>
            </div>
            <CheckCircle2 className="w-12 h-12 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">รายได้รวม</p>
              <p className="text-3xl font-bold mt-1">{formatPrice(totalRevenue)}</p>
            </div>
            <CreditCard className="w-12 h-12 text-orange-200" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="ค้นหาด้วยอีเมล, ชื่อ, หรือเบอร์โทร..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2.5 rounded-xl font-medium transition-all ${
                filterStatus === 'all'
                  ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/30'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              ทั้งหมด
            </button>
            <button
              onClick={() => setFilterStatus('verified')}
              className={`px-4 py-2.5 rounded-xl font-medium transition-all ${
                filterStatus === 'verified'
                  ? 'bg-green-600 text-white shadow-lg shadow-green-500/30'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              ชำระแล้ว
            </button>
            <button
              onClick={() => setFilterStatus('pending')}
              className={`px-4 py-2.5 rounded-xl font-medium transition-all ${
                filterStatus === 'pending'
                  ? 'bg-yellow-600 text-white shadow-lg shadow-yellow-500/30'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              รอตรวจสอบ
            </button>
          </div>
        </div>
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">ไม่พบคำสั่งซื้อ</h3>
            <p className="text-gray-600">ยังไม่มีคำสั่งซื้อในระบบ</p>
          </div>
        ) : (
          filteredOrders.map((order) => (
            <div
              key={order.id}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                {/* Left: Customer Info */}
                <div className="flex-1 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-5 h-5 text-purple-600" />
                        <h3 className="text-lg font-bold text-gray-900">{order.fullName}</h3>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <Mail className="w-4 h-4" />
                          {order.email}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Phone className="w-4 h-4" />
                          {order.phone}
                        </div>
                      </div>
                    </div>
                    {getStatusBadge(order.status)}
                  </div>

                  {/* Order Items */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">รายการสินค้า:</p>
                    <div className="space-y-2">
                      {order.items.map((item, index) => (
                        <div key={index} className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{item.name}</p>
                            <p className="text-sm text-gray-600">{item.version}</p>
                            {item.discountCode && (
                              <div className="flex items-center gap-1.5 mt-1">
                                <Tag className="w-3.5 h-3.5 text-orange-600" />
                                <span className="text-xs font-medium text-orange-600">
                                  {item.discountCode}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            {item.finalPrice < item.price ? (
                              <>
                                <p className="text-sm text-gray-400 line-through">
                                  {formatPrice(item.price)}
                                </p>
                                <p className="font-bold text-green-600">
                                  {formatPrice(item.finalPrice)}
                                </p>
                              </>
                            ) : (
                              <p className="font-bold text-gray-900">
                                {formatPrice(item.price)}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right: Payment Info */}
                <div className="lg:w-64 space-y-3">
                  <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4">
                    <p className="text-sm text-gray-600 mb-1">ยอดรวม</p>
                    {order.amount < order.originalAmount ? (
                      <>
                        <p className="text-lg text-gray-400 line-through">
                          {formatPrice(order.originalAmount)}
                        </p>
                        <p className="text-3xl font-bold text-purple-600">
                          {formatPrice(order.amount)}
                        </p>
                        <p className="text-sm text-green-600 font-medium mt-1">
                          ประหยัด {formatPrice(order.originalAmount - order.amount)}
                        </p>
                      </>
                    ) : (
                      <p className="text-3xl font-bold text-purple-600">
                        {formatPrice(order.amount)}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <CreditCard className="w-4 h-4" />
                      <span>{getPaymentMethodLabel(order.paymentMethod)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(order.createdAt)}</span>
                    </div>
                    {order.discountCode && (
                      <div className="flex items-center gap-2 text-orange-600 font-medium">
                        <Tag className="w-4 h-4" />
                        <span>{order.discountCode}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
